import { useCallback, useEffect, useRef, useState } from 'react'

import { t, term } from '../i18n'
import { longDate } from '../lib/format'
import { useBackButton } from '../lib/navigation'
import { haptic } from '../lib/telegram'
import type { Photo, PhotoAngle } from '../lib/types'
import { Button } from './Card'
import { Segmented } from './fields'
import { Icon } from './Icon'
import { usePhotoUrl } from './PhotoImage'
import { PoseGuide } from './PoseGuide'

const MAX_SIDE = 2000
const QUALITY = 0.85
const GHOST_OPACITY = 0.35

type CameraProps = {
  angle: PhotoAngle
  angles: PhotoAngle[]
  previous?: Photo
  saving: boolean
  onAngle: (angle: PhotoAngle) => void
  onSave: (blob: Blob) => void
  onFallback: () => void
  onClose: () => void
}

export function Camera({
  angle,
  angles,
  previous,
  saving,
  onAngle,
  onSave,
  onFallback,
  onClose,
}: CameraProps) {
  const video = useRef<HTMLVideoElement>(null)
  const [facing, setFacing] = useState<'user' | 'environment'>('user')
  const [compare, setCompare] = useState(false)
  const [shot, setShot] = useState<{ blob: Blob; url: string }>()
  const [ready, setReady] = useState(false)
  const [failed, setFailed] = useState(false)

  const ghost = usePhotoUrl(previous?.id)

  const mounted = useRef(true)
  const held = useRef<string | undefined>(undefined)

  useBackButton(onClose)

  useEffect(() => {
    held.current = shot?.url
  }, [shot])

  useEffect(
    () => () => {
      mounted.current = false
      if (held.current) URL.revokeObjectURL(held.current)
    },
    [],
  )

  useEffect(() => {
    if (shot) return undefined
    let stream: MediaStream | undefined
    let cancelled = false

    void navigator.mediaDevices
      .getUserMedia({ video: { facingMode: facing, width: { ideal: 1920 } } })
      .then((next) => {
        if (cancelled) {
          next.getTracks().forEach((track) => track.stop())
          return next
        }
        stream = next
        setFailed(false)
        const element = video.current
        if (element) {
          element.srcObject = next
          element.addEventListener('loadedmetadata', () => setReady(true), { once: true })
          if (element.videoWidth) setReady(true)
        }
        return next
      })
      .catch(() => setFailed(true))

    return () => {
      cancelled = true
      stream?.getTracks().forEach((track) => track.stop())
    }
  }, [facing, shot])

  const discard = useCallback(() => {
    setReady(false)
    setShot((current) => {
      if (current) URL.revokeObjectURL(current.url)
      return undefined
    })
  }, [])

  const capture = useCallback(() => {
    const element = video.current
    if (!element?.videoWidth) return

    const scale = Math.min(1, MAX_SIDE / Math.max(element.videoWidth, element.videoHeight))
    const canvas = document.createElement('canvas')
    canvas.width = Math.round(element.videoWidth * scale)
    canvas.height = Math.round(element.videoHeight * scale)

    const context = canvas.getContext('2d')
    if (!context) return
    context.drawImage(element, 0, 0, canvas.width, canvas.height)

    canvas.toBlob(
      (blob) => {
        if (!blob || !mounted.current) return
        haptic.select()
        setShot({ blob, url: URL.createObjectURL(blob) })
      },
      'image/jpeg',
      QUALITY,
    )
  }, [])

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-viewfinder">
      <div className="relative min-h-0 flex-1">
        {shot ? (
          <img src={shot.url} alt="" className="h-full w-full object-contain" />
        ) : (
          <>
            <video ref={video} autoPlay muted playsInline className="h-full w-full object-cover" />
            {ghost && compare ? (
              <img
                src={ghost}
                alt=""
                aria-hidden
                className="pointer-events-none absolute inset-0 h-full w-full object-cover"
                style={{ opacity: GHOST_OPACITY }}
              />
            ) : null}
            <div className="pointer-events-none absolute inset-x-4 bottom-[9%] top-[10%] flex justify-center text-on-accent/45">
              <PoseGuide angle={angle} />
            </div>
            <p className="pointer-events-none absolute inset-x-6 bottom-4 text-center text-hint text-on-accent drop-shadow-[0_1px_6px_var(--overlay-strong)]">
              {t.photos.pose[angle]}
            </p>
            {failed ? (
              <div className="absolute inset-x-6 top-1/2 flex flex-col items-center gap-3">
                <p className="text-center text-body text-on-accent">{t.photos.cameraFailed}</p>
                <button
                  type="button"
                  onClick={onFallback}
                  className="pressable flex h-tap items-center rounded-capsule border border-nav bg-nav px-[14px] text-hint text-text backdrop-blur-3"
                >
                  {t.photos.cameraFallback}
                </button>
              </div>
            ) : null}
          </>
        )}

        <div className="absolute inset-x-4 top-[calc(var(--spacing-safe-top)+16px)] flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={onClose}
            aria-label={t.photos.closeCamera}
            className="pressable flex h-tap w-tap items-center justify-center rounded-capsule border border-nav bg-nav text-text backdrop-blur-3"
          >
            <Icon name="x" size={18} />
          </button>

          <div className="flex items-center gap-2">
            {shot ? null : ghost ? (
              <button
                type="button"
                onClick={() => setCompare(!compare)}
                aria-label={t.photos.compare}
                aria-pressed={compare}
                className={`pressable flex h-tap w-tap items-center justify-center rounded-capsule border backdrop-blur-3 ${
                  compare ? 'border-accent bg-accent text-on-accent' : 'border-nav bg-nav text-text'
                }`}
              >
                <Icon name={compare ? 'eye' : 'eye-off'} size={18} />
              </button>
            ) : null}

            {shot ? null : (
              <button
                type="button"
                onClick={() => {
                  setReady(false)
                  setFacing(facing === 'user' ? 'environment' : 'user')
                }}
                aria-label={t.photos.flip}
                className="pressable flex h-tap w-tap items-center justify-center rounded-capsule border border-nav bg-nav text-text backdrop-blur-3"
              >
                <Icon name="refresh" size={18} />
              </button>
            )}
          </div>
        </div>
      </div>

      {shot ? (
        <div className="flex-none rounded-t-sheet-matte border-t border-hairline-soft bg-sheet-matte px-[14px] pb-[calc(10px+var(--spacing-safe-bottom))] pt-[14px]">
          <div className="text-sheet-title">{t.photos.keepTitle}</div>
          <div className="mt-[3px] text-hint text-text-muted">
            {previous
              ? `${t.photos.keepHint} ${longDate(previous.takenAt.slice(0, 10))}`
              : term('angle', angle)}
          </div>
          <div className="mt-3 flex gap-2">
            <div className="flex-1">
              <Button tone="secondary" onClick={discard} disabled={saving}>
                <Icon name="refresh" size={17} />
                {t.photos.retake}
              </Button>
            </div>
            <div className="flex-1">
              <Button onClick={() => onSave(shot.blob)} disabled={saving}>
                <Icon name="check" size={17} />
                {saving ? t.photos.uploading : t.photos.save}
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex-none px-4 pb-[calc(var(--spacing-safe-bottom)+16px)] pt-4">
          {ghost && compare ? (
            <div className="mb-[10px] text-center text-meta text-on-accent/70">
              {t.photos.comparing} {longDate(previous?.takenAt.slice(0, 10) ?? '')}
            </div>
          ) : null}

          <Segmented
            value={angle}
            onChange={(value) => {
              haptic.select()
              onAngle(value as PhotoAngle)
            }}
            options={angles.map((option) => ({ value: option, label: term('angle', option) }))}
            ariaLabel={t.photos.angleLabel}
          />

          <div className="mt-4 flex justify-center">
            <button
              type="button"
              onClick={capture}
              disabled={!ready}
              aria-label={t.photos.shutter}
              className="pressable flex h-[76px] w-[76px] items-center justify-center rounded-capsule border-[3px] border-on-accent disabled:opacity-disabled"
            >
              <span className="h-[60px] w-[60px] rounded-capsule bg-on-accent" />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
